/**
 * The live conversation screen (docs/04-screen-specs.md D3).
 *
 * This is the one screen where the network is in the critical path of the learner
 * actually speaking, so its failure behaviour is the design. A dropped socket
 * reconnects to the *same* Durable Object — same session id, conversation intact — and
 * when reconnection is exhausted the screen degrades to text rather than ending. The
 * learner finishes the conversation either way.
 */

import type { RouteResponse } from '@saysooner/api-client';
import {
  Banner,
  Button,
  ErrorState,
  MicButton,
  ScoreRing,
  Skeleton,
  Waveform,
} from '@saysooner/ui';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi, useApp } from '../app-context.js';
import { Recorder } from '../lib/audio.js';
import { API_BASE_URL, measuredScores } from '../lib/client.js';
import { type LiveServerMessage, LiveSession, type LiveState } from '../lib/live.js';
import { localVoice } from '../lib/local-voice.js';

export interface LiveConversationProps {
  readonly sessionId: string;
  readonly courseId: string;
  readonly title: string;
  /**
   * Which kind of conversation this is. The screen is the same for all of them — a
   * debate and a scenario are both a socket and a mic — but the questions worth asking
   * of each differ, so the mode-specific events are emitted alongside the generic ones.
   */
  readonly kind?: 'scenario' | 'open' | 'debate' | 'interview' | 'roleplay' | undefined;
  readonly onNavigate: (route: string) => void;
  /** BCP-47 tag for the target language, so a screen reader pronounces hints properly. */
  readonly lang?: string | undefined;
}

interface Turn {
  readonly role: 'tutor' | 'learner';
  readonly text: string;
  readonly partial: boolean;
}

export function LiveConversationScreen({
  sessionId,
  courseId,
  title,
  kind = 'scenario',
  onNavigate,
  lang,
}: LiveConversationProps): JSX.Element {
  const api = useApi();
  const { setInActiveSession, track } = useApp();

  const live = useRef<LiveSession | null>(null);
  const recorder = useRef<Recorder | null>(null);
  // The conversation's own counters. Refs, because the end event is emitted from a
  // cleanup function that must not depend on a render having happened first.
  const startedAt = useRef(Date.now());
  const turnCount = useRef(0);
  const turnStartedAt = useRef<number | null>(null);
  const trackRef = useRef(track);
  trackRef.current = track;

  const [state, setState] = useState<LiveState>({ kind: 'idle' });
  const [turns, setTurns] = useState<Turn[]>([]);
  const [recording, setRecording] = useState(false);
  const [level, setLevel] = useState(0);
  const [typed, setTyped] = useState('');
  const [mode, setMode] = useState<'voice' | 'text'>('voice');
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  /**
   * What the learner could say next, and the tutor's last line in their own language.
   * Both are asked for explicitly and both clear on the next tutor turn, because a
   * hint for a question that has moved on is worse than no hint.
   */
  const [hints, setHints] = useState<readonly { text: string; translation: string }[]>([]);
  const [translation, setTranslation] = useState<string | null>(null);
  const [waitingFor, setWaitingFor] = useState<'hint' | 'translation' | null>(null);
  const [personaName, setPersonaName] = useState<string | null>(null);
  /**
   * Corrections for the learner's last turn. Shown beside the reply rather than
   * interrupting it, and replaced wholesale by the next turn's.
   */
  const [corrections, setCorrections] = useState<
    readonly { original: string; corrected: string; explanation: string; severity: string }[]
  >([]);
  const [finished, setFinished] = useState(false);
  /**
   * Whether the tutor's words are on screen.
   *
   * Specified in `docs/02-user-flows.md` §5 and built on no client. Reading along is a
   * crutch in a speaking app, and hiding it is how a learner practises listening — so
   * this hides the *tutor's* lines only. What we heard the learner say always stays:
   * that is feedback about our own hearing, not a prompt they can lean on, and
   * concealing it is the opposite of the honesty W2 is built on.
   */
  const [tutorTextVisible, setTutorTextVisible] = useState(true);
  /*
   * The device's own voice, used only when the server says it has none.
   *
   * `tutor_text` carries `voiced` on its final frame, so this never races an audio
   * frame that is merely slow — it speaks when the server has already said it will not.
   */
  const voice = useRef(localVoice());
  const dialect = useRef('en-US');
  const spokenLine = useRef('');
  /**
   * The tutor's voice arrives in frames and plays once. Held in a ref rather than
   * state: a re-render per audio frame would be a re-render per few kilobytes.
   */
  const spoken = useRef<string[]>([]);

  /**
   * The frames that are not the conversation itself: help the learner asked for, the
   * tutor's voice, and the session's own bookkeeping.
   *
   * Split from `onMessage` because one switch over every frame in the protocol is a
   * function nobody can read, and the linter said so before a reviewer had to.
   */
  const onSideChannel = useCallback((message: LiveServerMessage): boolean => {
    switch (message.t) {
      case 'pong':
        // Evidence the socket is not merely open but answered. `LiveSession` counts the
        // unanswered pings; without this the heartbeat was write-only and a silently
        // dead connection looked healthy.
        live.current?.notePong();
        return true;

      case 'tutor_audio': {
        // Server audio wins outright: if any arrives, stop anything read locally.
        voice.current.stop();
        if (typeof message.b64 === 'string' && message.b64 !== '') {
          spoken.current.push(message.b64);
        }
        if (message.done === true) {
          const joined = spoken.current.join('');
          spoken.current = [];
          if (joined !== '') void playSpokenTurn(joined);
        }
        return true;
      }

      case 'correction':
        setCorrections(
          Array.isArray(message.corrections)
            ? (message.corrections as {
                original: string;
                corrected: string;
                explanation: string;
                severity: string;
              }[])
            : [],
        );
        return true;

      case 'ready':
        // The server's own view of the session. Until now the screen inferred it from
        // the socket opening, which is not the same thing: after a reconnect the socket
        // is open long before the Durable Object has reloaded the conversation.
        if (typeof message.personaName === 'string' && message.personaName.length > 0) {
          setPersonaName(message.personaName);
        }
        return true;

      case 'complete':
        // The server deciding the conversation is over — a turn budget reached, or the
        // session reaped. The screen used to ignore it and sit on a dead socket.
        setFinished(true);
        return true;

      case 'hint':
        setHints(
          Array.isArray(message.options)
            ? (message.options as { text: string; translation: string }[])
            : [],
        );
        setWaitingFor(null);
        return true;

      case 'translation':
        setTranslation(typeof message.text === 'string' ? message.text : '');
        setWaitingFor(null);
        return true;

      default:
        return false;
    }
  }, []);

  const onMessage = useCallback(
    (message: LiveServerMessage) => {
      if (onSideChannel(message)) return;

      switch (message.t) {
        case 'tutor_text': {
          const delta = typeof message.delta === 'string' ? message.delta : '';
          const isFinal = message.done === true;
          // Accumulated here as well as in the transcript, because the voice needs the
          // whole line and the transcript holds it only as rendered state.
          spokenLine.current =
            delta === '' && !isFinal ? spokenLine.current : spokenLine.current + delta;
          if (isFinal) {
            const line = spokenLine.current.trim();
            spokenLine.current = '';
            if (message.voiced === false && line !== '') {
              voice.current.speak(line, dialect.current);
            }
          }
          // A new tutor turn answers the old question, so a hint or translation of the
          // previous one is now misleading rather than merely stale.
          if (delta !== '') {
            setHints([]);
            setTranslation(null);
          }
          setTurns((current) => {
            const last = current[current.length - 1];
            // Streamed deltas append to an open tutor turn rather than creating one per
            // token, so the transcript reads as speech rather than as a token log.
            if (last !== undefined && last.role === 'tutor' && last.partial) {
              return [
                ...current.slice(0, -1),
                { role: 'tutor', text: last.text + delta, partial: !isFinal },
              ];
            }
            return [...current, { role: 'tutor', text: delta, partial: !isFinal }];
          });
          break;
        }

        case 'transcript': {
          const text = typeof message.text === 'string' ? message.text : '';
          setTurns((current) => [
            ...current,
            { role: 'learner', text, partial: message.partial === true },
          ]);
          break;
        }

        case 'quota':
          setSecondsLeft(
            typeof message.voiceSecondsLeft === 'number' ? message.voiceSecondsLeft : null,
          );
          break;

        case 'criterion_met':
          // The scenario's success criteria, as they are met. This is the only measure
          // of whether a conversation achieved what it was for, as opposed to merely
          // lasting a while.
          trackRef.current('live_criteria_met', {
            criterion: typeof message.id === 'string' ? message.id : 'unknown',
          });
          break;

        case 'degrade':
          // The server dropped us to text. Say why rather than silently changing the UI.
          setMode('text');
          break;

        default:
          // Unknown message types are skipped: a protocol addition must not brick an
          // older client mid-conversation.
          break;
      }
    },
    [onSideChannel],
  );

  useEffect(() => {
    setInActiveSession(true);
    trackRef.current('live_session_start', { mode: 'voice', scenario: title });
    let cancelled = false;

    void api.getTokens().then((tokens) => {
      if (cancelled || tokens === null) return;
      const session = new LiveSession({
        baseUrl: API_BASE_URL,
        sessionId,
        courseId,
        accessToken: tokens.accessToken,
        onMessage,
        onState: setState,
      });
      live.current = session;
      session.connect();
    });

    return () => {
      cancelled = true;
      setInActiveSession(false);
      // Emitted from the cleanup, so a conversation ended by navigating away is
      // counted the same as one ended by the button. Leaving is the common case and
      // the one worth knowing about.
      const seconds = Math.round((Date.now() - startedAt.current) / 1000);
      trackRef.current('live_session_end', {
        duration_s: seconds,
        turns: turnCount.current,
        reason: 'closed',
      });

      if (kind === 'debate') {
        trackRef.current('debate_end', { turns: turnCount.current, seconds });
      } else if (kind === 'interview') {
        trackRef.current('interview_end', { turns: turnCount.current, seconds });
      } else if (kind === 'open') {
        trackRef.current('open_talk_end', {
          turns: turnCount.current,
          seconds,
          // Counting subject changes needs the server's own view of the transcript.
          subject_changes: null,
        });
      }
      live.current?.close('user');
      void recorder.current?.close();
    };
  }, [api, sessionId, courseId, title, kind, onMessage, setInActiveSession]);

  const startSpeaking = useCallback(async () => {
    turnStartedAt.current = Date.now();
    const instance = new Recorder({ onLevel: setLevel });
    recorder.current = instance;
    try {
      await instance.open();
      instance.start();
      setRecording(true);
    } catch {
      // A mic that will not open degrades to text rather than leaving a dead button.
      setMode('text');
      setRecording(false);
    }
  }, []);

  const stopSpeaking = useCallback(async () => {
    setRecording(false);
    const instance = recorder.current;
    if (instance === null) return;
    try {
      const result = await instance.stop();
      const buffer = await result.blob.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      let binary = '';
      for (const byte of bytes) binary += String.fromCharCode(byte);
      live.current?.sendAudio(btoa(binary));
      live.current?.finishTurn();

      turnCount.current += 1;

      // The same turn, reported under the mode's own name as well. A debate turn and an
      // open-talk turn are different exercises with different questions attached: one is
      // about rebuttals, the other about whether the learner ran dry.
      if (kind === 'debate') {
        trackRef.current('debate_turn', {
          index: turnCount.current,
          // Scored server-side on argument structure; not this screen's to claim.
          rebuttal_quality: null,
        });
      } else if (kind === 'interview') {
        trackRef.current('interview_question', { index: turnCount.current, competency: null });
      } else if (kind === 'open') {
        trackRef.current('open_talk_turn', {
          index: turnCount.current,
          // "Stalled" means the learner went quiet rather than finished, which only the
          // silence watchdog can tell us; absent until it reports.
          stalled: null,
        });
      }
      // `score` is a present null: the score arrives asynchronously over the socket
      // and is not this turn's to report. The latency is, and it is the number that
      // decides whether a live conversation feels alive.
      trackRef.current('live_session_turn', {
        n: turnCount.current,
        score: null,
        latency_ms: turnStartedAt.current === null ? 0 : Date.now() - turnStartedAt.current,
      });
      turnStartedAt.current = null;
    } finally {
      await instance.close();
    }
  }, [kind]);

  return (
    <main id="main" className="vl-page vl-live">
      <header className="vl-live__header">
        <h1>{personaName === null ? title : `${title} · ${personaName}`}</h1>
        <Button
          variant="ghost"
          onClick={() => {
            live.current?.close('user');
            onNavigate('/talk');
          }}
        >
          End
        </Button>
      </header>

      <ConnectionNotice state={state} onDegrade={() => setMode('text')} />

      {secondsLeft !== null && secondsLeft < 120 && (
        // Warned before it runs out, not after. Being cut off mid-sentence is the
        // experience we are specifically avoiding.
        <Banner tone="warning">
          About {Math.max(0, Math.round(secondsLeft / 60))} minutes of speaking time left today.
          Review and mistakes stay open either way.
        </Banner>
      )}

      <div className="vl-live__transcript-controls">
        <Button
          variant="ghost"
          onClick={() => {
            trackRef.current('live_transcript_toggle', { visible: !tutorTextVisible });
            setTutorTextVisible(!tutorTextVisible);
          }}
        >
          {tutorTextVisible ? 'Hide the words' : 'Show the words'}
        </Button>
      </div>

      <ol className="vl-live__transcript">
        {turns.map((turn, index) => (
          <li
            // biome-ignore lint/suspicious/noArrayIndexKey: turns are append-only and a learner can legitimately repeat themselves, so position is their identity.
            key={`${turn.role}-${index}`}
            className="vl-live__turn"
            data-role={turn.role}
            data-partial={turn.partial}
            data-hidden={turn.role === 'tutor' && !tutorTextVisible}
          >
            {turn.role === 'tutor' && !tutorTextVisible ? '· · ·' : turn.text}
          </li>
        ))}
      </ol>

      {finished && (
        <Banner tone="info">
          This conversation is finished.{' '}
          <Button variant="ghost" onClick={() => onNavigate('/talk')}>
            See how it went
          </Button>
        </Banner>
      )}

      {corrections.length > 0 && (
        <ul className="vl-live__corrections" aria-label="Corrections">
          {corrections.map((correction) => (
            <li key={correction.original} data-severity={correction.severity}>
              <s>{correction.original}</s> <strong>{correction.corrected}</strong>
              <span className="vl-live__correction-why">{correction.explanation}</span>
            </li>
          ))}
        </ul>
      )}

      {translation !== null && translation.length > 0 && (
        <p className="vl-live__translation">{translation}</p>
      )}

      {hints.length > 0 && (
        <ul className="vl-live__hints" aria-label="Things you could say">
          {hints.map((option) => (
            <li key={option.text}>
              <button
                className="vl-live__hint"
                type="button"
                onClick={() => {
                  // Tapping a hint says it, rather than only showing it. A learner who
                  // has read the words still has to produce them in voice mode, so this
                  // sends only where it is the learner's actual input channel.
                  trackRef.current('live_hint_use', { kind });
                  if (mode === 'text') {
                    live.current?.sendText(option.text);
                    setTurns((current) => [
                      ...current,
                      { role: 'learner', text: option.text, partial: false },
                    ]);
                    setHints([]);
                  } else {
                    setTyped(option.text);
                    setMode('text');
                  }
                }}
              >
                <span lang={lang}>{option.text}</span>
                <span className="vl-live__hint-gloss">{option.translation}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="vl-live__assists">
        <Button
          variant="ghost"
          disabled={waitingFor !== null || state.kind !== 'open'}
          onClick={() => {
            setWaitingFor('hint');
            trackRef.current('live_hint_request', { kind });
            live.current?.send({ t: 'hint' });
          }}
        >
          {waitingFor === 'hint' ? 'Thinking…' : 'What could I say?'}
        </Button>
        <Button
          variant="ghost"
          disabled={waitingFor !== null || state.kind !== 'open'}
          onClick={() => {
            setWaitingFor('translation');
            trackRef.current('live_translate_request', { kind });
            live.current?.send({ t: 'translate' });
          }}
        >
          {waitingFor === 'translation' ? 'Translating…' : 'What did that mean?'}
        </Button>
        <Button
          variant="ghost"
          disabled={state.kind !== 'open'}
          onClick={() => {
            trackRef.current('live_repeat_request', { kind });
            live.current?.send({ t: 'repeat' });
          }}
        >
          Say that again
        </Button>
        <Button
          variant="ghost"
          disabled={state.kind !== 'open'}
          onClick={() => {
            // Re-read rather than replayed: slowing a recording pitches it down, while
            // a slower synthesis is the same voice speaking more clearly.
            trackRef.current('live_slow_request', { kind });
            live.current?.send({ t: 'slow' });
          }}
        >
          Slower
        </Button>
      </div>

      {mode === 'voice' ? (
        <div className="vl-live__controls">
          {recording && <Waveform level={level} />}
          <MicButton
            state={recording ? 'listening' : state.kind === 'open' ? 'idle' : 'disabled'}
            mode="toggle"
            level={level}
            onStart={() => void startSpeaking()}
            onStop={() => void stopSpeaking()}
          />
          <Button variant="ghost" onClick={() => setMode('text')}>
            Type instead
          </Button>
        </div>
      ) : (
        <form
          className="vl-live__compose"
          onSubmit={(event) => {
            event.preventDefault();
            if (typed.trim().length === 0) return;
            live.current?.sendText(typed);
            setTurns((current) => [...current, { role: 'learner', text: typed, partial: false }]);
            setTyped('');
          }}
        >
          <input
            className="vl-field__input"
            value={typed}
            placeholder="Type your reply"
            onChange={(event) => setTyped(event.target.value)}
          />
          <Button type="submit" disabled={typed.trim().length === 0}>
            Send
          </Button>
        </form>
      )}
    </main>
  );
}

/**
 * Connection state, said plainly.
 *
 * Reconnecting is *not* an error: the session is intact on the server, and saying so
 * stops a learner from closing the app and losing the turn they were mid-way through.
 */
function ConnectionNotice({
  state,
  onDegrade,
}: {
  readonly state: LiveState;
  readonly onDegrade: () => void;
}): JSX.Element | null {
  switch (state.kind) {
    case 'connecting':
      return <Banner tone="info">Connecting…</Banner>;

    case 'reconnecting':
      return (
        <Banner tone="warning">
          Reconnecting (attempt {state.attempt}). Your conversation is saved — nothing is lost.
        </Banner>
      );

    case 'closed':
      return state.recoverable ? (
        <ErrorState
          title="We couldn't reconnect"
          detail={state.reason}
          alternative={{ label: 'Carry on by typing', onClick: onDegrade }}
        />
      ) : null;

    default:
      return null;
  }
}

// ── D6 Conversation report card ─────────────────────────────────────────────

/**
 * The report card for a conversation, as distinct from a lesson's.
 *
 * A conversation is judged against its own success criteria, not only against a score:
 * "did you get the coffee ordered" is the thing the learner was trying to do, and a
 * five-dimension score with no criteria result answers a different question. The
 * criteria checklist comes first for that reason.
 *
 * `Try again harder` re-runs the same scenario with a stricter tutor. That is the
 * action a learner who did well actually wants, and offering only `Drill these` sends
 * everyone to remediation whether they need it or not.
 */
export function ConversationReportScreen({
  report,
  loading,
  onDrill,
  onRetryHarder,
  onPlayImproved,
  onDone,
}: {
  readonly report: RouteResponse<'getReport'> | null;
  readonly loading: boolean;
  readonly onDrill: () => void;
  readonly onRetryHarder: () => void;
  readonly onPlayImproved: (url: string) => void;
  readonly onDone: () => void;
}): JSX.Element {
  const { track } = useApp();

  useEffect(() => {
    if (report === null) return;
    track('conversation_report_view', {
      scores: measuredScores(report.dimensions),
      criteria_met: report.criteria.filter((criterion) => criterion.met).length,
    });
  }, [report, track]);

  if (loading || report === null) {
    return (
      <main id="main" className="vl-page">
        <Skeleton lines={6} label="Writing up your conversation" />
      </main>
    );
  }

  const spokenTurns = report.turns;

  return (
    <main id="main" className="vl-page vl-report">
      <h1 className="vl-report__title">
        {report.criteria.every((criterion) => criterion.met)
          ? 'You did what you set out to do'
          : 'Here is how that went'}
      </h1>

      {/* The criteria first. A conversation is measured against what it was for. */}
      <section aria-label="What you were trying to do">
        <ul className="vl-criteria">
          {report.criteria.map((criterion) => (
            <li key={criterion.id} data-met={criterion.met}>
              <span className="vl-criteria__mark" aria-hidden="true">
                {criterion.met ? '✓' : '·'}
              </span>
              <span>{criterion.description}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* An abandoned session still reports, and still counts the minutes spoken:
          telling someone who spoke for four minutes that there is nothing to show is
          the fastest way to stop them coming back. */}
      {spokenTurns === 0 ? (
        <Banner tone="info">
          You left before saying anything, so there is nothing to score — but the scenario is still
          there whenever you want it.
        </Banner>
      ) : (
        <section aria-label="Your scores">
          <ScoreRing score={report.overall} size="lg" label="Overall" />
          <ul className="vl-report__notes">
            {report.notes.map((note) => (
              <li key={note.dimension}>
                <strong>{note.dimension}</strong>
                {/* Prose written against what the learner actually said, which is the
                    only kind of feedback worth reading. */}
                <p>{note.text}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="vl-step__actions">
        {report.improvedSpeechUrl !== null && (
          <Button
            variant="secondary"
            onClick={() => {
              // Whether anyone plays this is the question: a model re-render of your
              // own best attempt is either the most useful thing on the screen or
              // decoration, and nothing else would tell us which.
              track('improved_speech_played', {});
              onPlayImproved(report.improvedSpeechUrl ?? '');
            }}
          >
            Hear it said well
          </Button>
        )}
        <Button onClick={onDrill}>Drill these</Button>
        <Button
          variant="secondary"
          onClick={() => {
            track('report_retry_harder', {});
            onRetryHarder();
          }}
        >
          Try again, harder
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Done
        </Button>
      </div>
    </main>
  );
}

/**
 * Plays one synthesised tutor turn.
 *
 * The frames carry base64 MP3, which every browser decodes without a codec of ours.
 * Failure is silent on purpose: the text of the turn is already on screen, and a
 * browser that refuses to autoplay audio must not surface as an error about the
 * conversation.
 */
async function playSpokenTurn(base64: string): Promise<void> {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: 'audio/mpeg' }));
    const audio = new Audio(url);
    // Revoked on both paths, or a long conversation leaks one object URL per turn.
    audio.addEventListener('ended', () => URL.revokeObjectURL(url));
    audio.addEventListener('error', () => URL.revokeObjectURL(url));
    await audio.play();
  } catch {
    // Autoplay refused, or an unplayable buffer. The turn is readable either way.
  }
}
